import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import api from '../../hooks/useApi';
import { useDarkMode } from '../../hooks/useDarkMode';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ width: 14, height: 14, color: '#9CA3AF', position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
  </svg>
);

// One place for every colour the panel uses, so light and dark both stay legible
// (result text used to be hardcoded near-black, which vanished in dark mode).
const theme = (isDark) => ({
  panel:     isDark ? 'rgb(30 41 59)'  : '#fff',
  border:    isDark ? 'rgb(51 65 85)'  : '#E5E7EB',
  hairline:  isDark ? 'rgba(148,163,184,0.14)' : '#F1F3F5',
  groupBg:   isDark ? 'rgba(15,23,42,0.55)'    : '#F8FAFC',
  title:     isDark ? '#F1F5F9' : '#111827',
  meta:      isDark ? '#94A3B8' : '#6B7280',
  faint:     isDark ? '#64748B' : '#9CA3AF',
  ref:       isDark ? '#93C5FD' : '#1E3A5F',
  money:     isDark ? '#34D399' : '#047857',
  rowHover:  isDark ? 'rgba(148,163,184,0.10)' : '#F8FAFC',
  rowActive: isDark ? 'rgba(59,130,246,0.18)'  : '#EFF6FF',
  rail:      isDark ? '#60A5FA' : '#1E3A5F',
  mark:      isDark ? 'rgba(253,224,71,0.28)'  : '#FEF08A',
  input:     isDark ? 'rgb(30 41 59)' : '#F9FAFB',
  pill: {
    muted: { bg: isDark ? 'rgba(148,163,184,0.16)' : '#F3F4F6', fg: isDark ? '#CBD5E1' : '#4B5563' },
    ok:    { bg: isDark ? 'rgba(16,185,129,0.18)'  : '#D1FAE5', fg: isDark ? '#6EE7B7' : '#065F46' },
    warn:  { bg: isDark ? 'rgba(217,119,6,0.20)'   : '#FEF3C7', fg: isDark ? '#FCD34D' : '#92400E' },
    off:   { bg: isDark ? 'rgba(148,163,184,0.12)' : '#F3F4F6', fg: isDark ? '#94A3B8' : '#9CA3AF' },
    info:  { bg: isDark ? 'rgba(59,130,246,0.20)'  : '#DBEAFE', fg: isDark ? '#93C5FD' : '#1E40AF' },
  },
});

// Status wording varies by entity; map it onto four tones so a busy result set
// reads at a glance instead of every status looking the same.
const OK_STATES   = ['active', 'approved', 'live', 'completed', 'delivered', 'resolved', 'issued', 'won', 'acknowledged'];
const WARN_STATES = ['submitted', 'under review', 'under_review', 'evaluating', 'draft', 'pending', 'responded', 'open', 'in progress', 'in_progress', 'part delivered', 'part_delivered'];
const OFF_STATES  = ['rejected', 'cancelled', 'closed', 'lost', 'inactive', 'no bid'];
function statusTone(status) {
  const v = String(status || '').toLowerCase().trim();
  if (OK_STATES.includes(v))   return 'ok';
  if (WARN_STATES.includes(v)) return 'warn';
  if (OFF_STATES.includes(v))  return 'off';
  return 'muted';
}


// ─── Teams ───────────────────────────────────────────────────────────────────
// Presence comes live from Teams via Graph. The dot colours match what people
// already recognise from Teams itself, so the meaning needs no explaining.
const PRESENCE_DOT = {
  ok:    '#10b981',   // available
  warn:  '#f59e0b',   // busy, away, in a meeting
  off:   '#ef4444',   // do not disturb
  muted: '#9ca3af',   // offline or unknown
};

// Teams deep links. Documented URL scheme, no API call and no permission
// needed, and they open the desktop app when it is installed.
const teamsChatUrl = (email) => `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(email)}`;
const teamsCallUrl = (email) => `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(email)}`;

// Last activity in THIS app, not in Teams. Graph exposes live presence but no
// "last seen", so anything claiming to be a Teams last-seen would be invented.
// Labelled "active" rather than "seen" to keep that honest.
function lastActive(ts) {
  if (!ts) return null;
  const then = new Date(String(ts).replace(' ', 'T') + (String(ts).endsWith('Z') ? '' : 'Z'));
  if (isNaN(then.getTime())) return null;
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 0) return null;
  if (mins < 2) return 'active just now';
  if (mins < 60) return `active ${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `active ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `active ${days} day${days === 1 ? '' : 's'} ago`;
  return null;   // beyond a month it is noise, not information
}

// ─── Whereabouts ─────────────────────────────────────────────────────────────
// The search box is the fastest way to ask "where is Dan?", so a person result
// answers it on the row: a pill for where they are, and supporting detail for
// which desk, which office, and whether they are in a meeting right now.
// Shorter than the full status label, because the pill is a narrow column.
// Anything not listed (an office's own custom status) falls back to the label
// and tone the API sends, so a new status needs no change here.
const WHEREABOUTS_PILL = {
  office:      'In office',
  home:        'Home',
  offsite:     'Out',
  not_working: 'Not working',
  leave:       'On leave',
};

const BUSY_LABEL = { busy: 'In a meeting', tentative: 'Tentatively busy', oof: 'Out of office' };

function busyText(busy) {
  const label = BUSY_LABEL[busy?.kind] || 'Busy';
  const end = busy?.until ? new Date(busy.until) : null;
  if (!end || isNaN(end.getTime())) return label;
  return `${label} until ${end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

// Returns { status, tone, meta } — meta leads with the meeting because the pill
// already says where they are, and a meeting is the time-sensitive part.
function whereaboutsView(w) {
  if (!w) return { status: null, tone: undefined, meta: [] };
  const label = WHEREABOUTS_PILL[w.code] || w.short_label || w.label || null;
  const meta = [];
  // Presence and free/busy answer different halves of the same question, so
  // they are merged into one phrase: "In a meeting until 14:30".
  if (w.presence && w.busy) {
    const until = busyText(w.busy).match(/until .+$/)?.[0];
    meta.push(until ? `${w.presence.label} ${until}` : w.presence.label);
  } else if (w.presence) meta.push(w.presence.label);
  else if (w.busy) meta.push(busyText(w.busy));
  if (w.detail) meta.push(w.slot ? `${w.detail} · ${w.slot}` : w.detail);
  else if (w.slot) meta.push(w.slot);
  meta.push(...(w.extras || []));
  return {
    // With no location known, the only thing we have is the meeting, which is
    // still worth surfacing on its own.
    status: label || (w.busy ? BUSY_LABEL[w.busy.kind] || 'Busy' : null),
    tone: w.tone || 'warn',
    meta,
  };
}

// Most amounts are sterling; a purchase order can be raised in EUR/USD, so pass
// its currency rather than stamping a £ on a foreign figure.
function fmtAmt(v, currency = 'GBP') {
  if (v == null) return null;
  const code = String(currency || 'GBP').toUpperCase();
  const sym = { GBP: '£', EUR: '€', USD: '$' }[code];
  const amount = Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return sym ? sym + amount : `${code} ${amount}`;
}

// Tint the part of a label that matched, so it is obvious why a row came back.
function Highlight({ text, query, mark }) {
  const s = String(text ?? '');
  const q = (query || '').trim();
  if (q.length < 2) return s;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return s;
  return (
    <>
      {s.slice(0, i)}
      <span style={{ background: mark, borderRadius: 2, padding: '0 1px' }}>{s.slice(i, i + q.length)}</span>
      {s.slice(i + q.length)}
    </>
  );
}

// Every result renders through here, so all 19 categories line up: reference and
// name on the first line, supporting detail on the second, money and status
// pinned to the right rail.
function ResultRow({ item, c, query, active, onHover, onSelect, rowRef }) {
  const meta = (item.meta || [])
    .map(m => (typeof m === 'string' ? { t: m } : m))
    .filter(m => m && String(m.t ?? '').trim());
  const pill = c.pill[item.tone || statusTone(item.status)] || c.pill.muted;

  return (
    <div
      ref={rowRef}
      onMouseDown={e => { e.preventDefault(); onSelect(item); }}
      onMouseEnter={onHover}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '8px 12px 8px 10px', cursor: 'pointer',
        borderLeft: `2px solid ${active ? c.rail : 'transparent'}`,
        background: active ? c.rowActive : 'transparent',
        borderBottom: `1px solid ${c.hairline}`,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, whiteSpace: 'nowrap' }}>
          {/* Teams presence, using the dot colours people already read without
              thinking from Teams itself. */}
          {item.presence && (
            <span
              title={`Teams: ${item.presence.label}`}
              style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: PRESENCE_DOT[item.presence.tone] || PRESENCE_DOT.muted,
                boxShadow: '0 0 0 1.5px rgba(255,255,255,0.6)',
              }}
            />
          )}
          {item.ref && (
            <span style={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 700, color: c.ref, flexShrink: 0 }}>
              <Highlight text={item.ref} query={query} mark={c.mark} />
            </span>
          )}
          <span style={{ minWidth: 0, fontSize: 12, fontWeight: 600, color: c.title, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <Highlight text={item.title} query={query} mark={c.mark} />
          </span>
        </div>
        {meta.length > 0 && (
          <div style={{ marginTop: 3, fontSize: 10, color: c.meta, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {meta.map((m, i) => (
              <span key={i}>
                {i > 0 && <span style={{ color: c.faint, margin: '0 6px' }}>·</span>}
                <span style={m.mono ? { fontFamily: MONO } : undefined}>
                  <Highlight text={m.t} query={query} mark={c.mark} />
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Reaching somebody is the usual next step after finding them, so the
          chat and call links live on the row rather than a click away. They are
          plain Teams deep links: no API call, no permission, and they open the
          desktop app when it is installed. onMouseDown stops the row's own
          navigate handler firing underneath. */}
      {item.teamsEmail && (
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          <a
            href={teamsChatUrl(item.teamsEmail)}
            target="_blank"
            rel="noreferrer"
            title={`Message ${item.title} in Teams`}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            style={{
              fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 999,
              border: `1px solid ${c.border}`, color: c.ref, textDecoration: 'none', whiteSpace: 'nowrap',
            }}
          >Chat</a>
          <a
            href={teamsCallUrl(item.teamsEmail)}
            target="_blank"
            rel="noreferrer"
            title={`Call ${item.title} in Teams`}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            style={{
              fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 999,
              border: `1px solid ${c.border}`, color: c.ref, textDecoration: 'none', whiteSpace: 'nowrap',
            }}
          >Call</a>
        </div>
      )}

      {(item.amount || item.status) && (
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          {item.amount && (
            <span style={{ fontSize: 11, fontWeight: 700, color: c.money, whiteSpace: 'nowrap' }}>
              {item.amount.text}
              {item.amount.note && (
                <span style={{ fontSize: 8.5, fontWeight: 500, color: c.faint, marginLeft: 3 }}>{item.amount.note}</span>
              )}
            </span>
          )}
          {item.status && (
            <span style={{
              fontSize: 8, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
              padding: '2px 6px', borderRadius: 999, whiteSpace: 'nowrap',
              background: pill.bg, color: pill.fg,
            }}>
              {item.status}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function GlobalSearch({ onNavigate }) {
  const isDark = useDarkMode();
  const c = useMemo(() => theme(isDark), [isDark]);
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const [cat, setCat]         = useState('all');
  const [activeIdx, setActiveIdx] = useState(0);
  const [expanded, setExpanded]   = useState(() => new Set()); // categories fully fetched
  const [expanding, setExpanding] = useState(null);
  // Default to live/in-flight work only — completed work is rarely revisited.
  const [includeClosed, setIncludeClosed] = useState(false);
  const timerRef      = useRef(null);
  const containerRef  = useRef(null);
  const activeRowRef  = useRef(null);
  const activeQueryRef = useRef('');

  const runSearch = useCallback(async (q, closed) => {
    activeQueryRef.current = q;
    setLoading(true);
    try {
      const data = await api.get('search', { q, limit: 8, ...(closed ? { include_closed: 1 } : {}) });
      if (activeQueryRef.current === q) {
        setResults(data);
        setExpanded(new Set());
        setCat('all');
        setActiveIdx(0);
      }
    } catch {
      if (activeQueryRef.current === q) setResults(null);
    } finally {
      if (activeQueryRef.current === q) setLoading(false);
    }
  }, []);

  // Pull the rest of one category. Each category is capped so the dropdown stays
  // quick; when the count says rows were held back, this fetches them rather
  // than leaving the user to guess what they missed.
  const loadAll = useCallback(async (key, closed) => {
    const q = activeQueryRef.current;
    if (!q) return;
    setExpanding(key);
    try {
      const data = await api.get('search', { q, category: key, limit: 50, ...(closed ? { include_closed: 1 } : {}) });
      if (activeQueryRef.current !== q) return;
      setResults(prev => (prev ? {
        ...prev,
        [key]: data[key] || [],
        totals: { ...(prev.totals || {}), ...(data.totals || {}) },
      } : prev));
      setExpanded(prev => new Set(prev).add(key));
    } catch { /* leave the capped rows in place */ }
    finally { setExpanding(null); }
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (query.trim().length >= 2) {
      timerRef.current = setTimeout(() => runSearch(query.trim(), includeClosed), 280);
    } else {
      activeQueryRef.current = '';
      setResults(null);
      setLoading(false);
    }
    return () => clearTimeout(timerRef.current);
  }, [query, includeClosed, runSearch]);

  useEffect(() => {
    const close = e => { if (!containerRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const clear = () => { setQuery(''); setResults(null); setOpen(false); setCat('all'); setIncludeClosed(false); };

  // Reset only on a new search (handled in runSearch) — not on any change to
  // `results`, or expanding a category would bounce the view back to "All".
  useEffect(() => { setActiveIdx(0); }, [cat]);

  // Keep the keyboard-highlighted row in view as it moves past the fold.
  useEffect(() => { activeRowRef.current?.scrollIntoView({ block: 'nearest' }); }, [activeIdx]);

  // Build render-ready items per category: reference, name, supporting detail,
  // and where relevant a value and a status.
  const people = (results?.people || []).map(p => {
    const w = whereaboutsView(p.whereabouts);
    // Only shown when Teams presence says nothing, so the row never claims
    // somebody is offline AND was active a minute ago.
    const active = p.whereabouts?.presence ? null : lastActive(p.last_login);
    return {
      // The id is what selects this person on arrival: by id, never by name,
      // so "anna" opens Anna Webster and not Hannah Smart.
      id: p.id, _nav: 'people', _id: p.id,
      title: `${p.first_name || ''} ${p.last_name || ''}`.trim(),
      meta: [...w.meta, active, p.email_work],
      status: w.status, tone: w.tone,
      presence: p.whereabouts?.presence || null,
      teamsEmail: p.email_work || null,
    };
  });

  const bids = (results?.bids || []).map(b => ({
    id: b.id, _nav: 'bid-detail', _id: b.id,
    ref: b.reference, title: b.opportunity,
    meta: [b.kind === 'lead' ? 'Lead' : null, b.buyer, b.service_line],
    status: b.status,
  }));

  const sows = (results?.sows || []).map(s => ({
    // Full-access roles get the full SOW; everyone else the read-only overview.
    id: s.id, _nav: s.readonly ? 'sow-overview' : 'sow-detail', _id: s.id,
    ref: s.reference, title: s.name,
    meta: [s.client_name, s.po_number ? { t: `PO: ${s.po_number}`, mono: true } : null,
           s.sourcing_event_id ? { t: `SE: ${s.sourcing_event_id}`, mono: true } : null],
    amount: s.total_excl_vat > 0 ? { text: fmtAmt(s.total_excl_vat), note: 'excl VAT' } : null,
    status: s.status,
  }));

  const milestones = (results?.milestones || []).map(m => ({
    id: m.id, _nav: 'sow-detail', _id: m.sow_id, _sub: m.id ? `milestone-${m.id}` : undefined,
    ref: m.milestone_code, title: m.description,
    meta: [m.invoice_number ? { t: m.invoice_number, mono: true } : null, m.sow_ref],
    amount: m.invoice_amount_incl > 0 ? { text: fmtAmt(m.invoice_amount_incl), note: 'incl VAT' }
      : m.invoice_amount_excl > 0 ? { text: fmtAmt(m.invoice_amount_excl), note: 'excl VAT' } : null,
  }));

  const inventory = (results?.inventory || []).map(i => ({
    id: i.id, _nav: 'inventory', _id: i.id,
    ref: i.asset_tag || i.part_number || i.serial_number, title: i.name,
    meta: [i.serial_number ? { t: `SN: ${i.serial_number}`, mono: true } : null,
           i.part_number ? { t: `PN: ${i.part_number}`, mono: true } : null],
  }));

  const catalog = (results?.catalog || []).map(p => ({
    id: p.id, _nav: 'product-catalog', _id: p.id,
    ref: p.code, title: p.description,
    meta: [p.manufacturer_part_number ? { t: p.manufacturer_part_number, mono: true } : null],
  }));

  const pos = (results?.pos || []).map(p => ({
    id: p.id, _nav: 'pos', _id: p.id,
    ref: p.po_number, title: p.supplier_name,
    meta: [p.matched_invoice ? { t: `Inv: ${p.matched_invoice}`, mono: true } : null],
    amount: p.total > 0 ? { text: fmtAmt(p.total, p.currency), note: 'incl VAT' } : null,
    status: p.status,
  }));

  const clients = (results?.clients || []).map(cl => ({
    id: cl.id, _nav: 'clients',
    title: cl.name,
    meta: [cl.code ? { t: cl.code, mono: true } : null, cl.contact_email],
  }));

  const suppliers = (results?.suppliers || []).map(s => ({
    id: s.id, _nav: 'suppliers',
    title: s.name,
    meta: [s.code ? { t: s.code, mono: true } : null, s.contact_name],
  }));

  // Supplier invoices from the accounts inbox. The ops reference is shown as the
  // row's reference because it is what somebody pastes in from Sage; the row
  // opens the email the invoice was logged from.
  const supplierInvoices = (results?.supplier_invoices || []).map(si => ({
    id: si.id, _nav: 'email-inbox', _id: si.email_id || undefined,
    ref: si.ops_reference, title: si.supplier_name || si.invoice_number,
    meta: [si.invoice_number ? { t: si.invoice_number, mono: true } : null,
           si.sage_invoice_number ? { t: `Sage: ${si.sage_invoice_number}`, mono: true } : null,
           si.invoice_date],
    amount: si.total_incl_vat > 0 ? { text: fmtAmt(si.total_incl_vat), note: 'incl VAT' } : null,
    status: si.sage_invoice_id ? 'In Sage' : si.status,
  }));

  const risks = (results?.risks || []).map(r => ({
    id: r.id, _nav: 'risks',
    ref: r.reference, title: r.title, meta: [r.sow_ref],
  }));

  const ccns = (results?.ccns || []).map(cn => ({
    id: cn.id, _nav: 'change-register',
    ref: cn.ccn_number, title: cn.title, meta: [cn.sow_ref],
  }));

  const crmContacts = (results?.crm_contacts || []).map(ct => ({
    id: ct.id, _nav: 'crm-contacts',
    title: `${ct.first_name || ''} ${ct.last_name || ''}`.trim(),
    meta: [ct.job_title, ct.client_name],
  }));

  const hardware = (results?.hardware || []).map(h => ({
    id: h.id, _nav: 'hardware-requests',
    title: h.product_name,
    meta: [h.part_number ? { t: h.part_number, mono: true } : null, h.requester_name?.trim()],
  }));

  const resourceWorkers = (results?.resource_workers || []).map(w => ({
    id: w.id, _nav: 'contractor-dashboard', _id: w.id,
    title: `${w.first_name || ''} ${w.last_name || ''}`.trim(),
    meta: [w.email],
    status: w.is_active === 0 ? 'inactive' : null,
  }));

  const resourceContracts = (results?.resource_contracts || []).map(rc => ({
    id: rc.id, _nav: 'contractor-dashboard', _id: rc.resource_worker_id,
    ref: rc.reference, title: rc.worker_name?.trim(),
    meta: [rc.role_description, rc.supplier_name],
    status: rc.status,
  }));

  const qmsDocs = (results?.qms_docs || []).map(d => ({
    id: d.id, _nav: 'qms-library',
    ref: d.doc_number, title: d.title,
    meta: [d.owner_name, d.category],
  }));

  const comments = (results?.comments || []).map(cm => ({
    id: cm.id, _nav: cm.sow_id ? 'sow-detail' : null, _id: cm.sow_id,
    title: `“${(cm.content || '').slice(0, 90)}${(cm.content || '').length > 90 ? '…' : ''}”`,
    meta: [cm.person_name, cm.sow_ref ? { t: cm.sow_ref, mono: true } : null,
           !cm.sow_ref ? cm.entity_type : null],
  }));

  const actionItems = (results?.actions || []).map(a => ({
    id: a.id, _nav: a.sow_id ? 'sow-detail' : null, _id: a.sow_id,
    title: a.title,
    meta: [a.sow_ref ? { t: a.sow_ref, mono: true } : null, a.owner_name],
    status: a.status,
  }));

  // Ordered category descriptors — drives both the filter chips and the groups
  const categories = [
    { key: 'people',       title: 'People',            items: people },
    { key: 'clients',      title: 'Clients',           items: clients },
    { key: 'crm_contacts', title: 'CRM Contacts',      items: crmContacts },
    { key: 'bids',         title: 'Pipeline Tracker',  items: bids },
    { key: 'sows',         title: 'SOW Tracker',       items: sows },
    { key: 'milestones',   title: 'Milestones',        items: milestones },
    { key: 'actions',      title: 'Actions',           items: actionItems },
    { key: 'comments',     title: 'Comments',          items: comments },
    { key: 'ccns',         title: 'Change Notes',      items: ccns },
    { key: 'risks',        title: 'Risks',             items: risks },
    { key: 'pos',          title: 'Purchase Orders',   items: pos },
    { key: 'supplier_invoices', title: 'Supplier Invoices', items: supplierInvoices },
    { key: 'hardware',     title: 'Hardware Requests', items: hardware },
    { key: 'resource_contracts', title: 'Resource Contracts', items: resourceContracts },
    { key: 'resource_workers',   title: 'External Resources', items: resourceWorkers },
    { key: 'suppliers',    title: 'Suppliers',         items: suppliers },
    { key: 'inventory',    title: 'Inventory',         items: inventory },
    { key: 'catalog',      title: 'Product Catalog',   items: catalog },
    { key: 'qms_docs',     title: 'QMS Documents',     items: qmsDocs },
  ]
    // `total` is how many actually matched; `items` is what came back under the
    // per-category cap. Showing both is the point — a truncated category must
    // never read as a complete answer.
    .map(g => ({ ...g, total: results?.totals?.[g.key] ?? g.items.length }))
    .filter(g => g.items.length);

  const totalCount = categories.reduce((n, g) => n + g.total, 0);
  const hasAny = totalCount > 0;
  const visibleCategories = cat === 'all' ? categories : categories.filter(g => g.key === cat);

  // Flat index across the visible groups, so ↑/↓ walk the whole list.
  let cursor = 0;
  const groups = visibleCategories.map(g => { const start = cursor; cursor += g.items.length; return { ...g, start }; });
  const flatItems = groups.flatMap(g => g.items);

  const onSelect = (i) => { if (i._nav) onNavigate(i._nav, i._id, i._sub); clear(); };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { clear(); return; }
    if (!flatItems.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => (i + 1) % flatItems.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => (i - 1 + flatItems.length) % flatItems.length); }
    else if (e.key === 'Enter') {
      const pick = flatItems[activeIdx];
      if (pick) { e.preventDefault(); onSelect(pick); }
    }
  };

  const chipStyle = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 9.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999, cursor: 'pointer',
    border: `1px solid ${active ? (isDark ? 'rgb(96 165 250)' : '#1e3a5f') : c.border}`,
    background: active ? (isDark ? 'rgb(30 58 95)' : '#1e3a5f') : (isDark ? 'rgb(30 41 59)' : '#fff'),
    color: active ? '#fff' : c.meta,
    whiteSpace: 'nowrap',
  });
  const chipCount = (active) => ({
    fontSize: 8.5, fontWeight: 800, padding: '0 4px', borderRadius: 999,
    background: active ? 'rgba(255,255,255,0.22)' : (isDark ? 'rgba(148,163,184,0.18)' : '#F3F4F6'),
    color: active ? '#fff' : c.faint,
  });

  return (
    <div ref={containerRef} className="global-search" style={{ position: 'relative', width: 260, flexShrink: 0 }}>
      <div style={{ position: 'relative' }}>
        <SearchIcon />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search people, projects, assets..."
          style={{
            width: '100%', height: 30, paddingLeft: 30, paddingRight: query ? 24 : 8,
            fontSize: 10.5, border: `1px solid ${c.border}`, borderRadius: 7,
            background: c.input, outline: 'none', fontFamily: 'inherit',
            boxSizing: 'border-box', color: isDark ? 'rgb(241 245 249)' : '#111',
          }}
        />
        {query && (
          <button
            onMouseDown={e => { e.preventDefault(); clear(); }}
            title="Clear search"
            style={{
              position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
              border: 'none', background: 'none', cursor: 'pointer', padding: 2,
              fontSize: 13, lineHeight: 1, color: c.faint,
            }}
          >&times;</button>
        )}
      </div>

      {open && query.trim().length >= 2 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 5,
          width: 560, maxWidth: 'calc(100vw - 24px)',
          background: c.panel, border: `1px solid ${c.border}`, borderRadius: 10,
          boxShadow: isDark ? '0 16px 40px rgba(0,0,0,0.45)' : '0 16px 40px rgba(13,29,50,0.16)',
          zIndex: 9999, maxHeight: 480, overflow: 'hidden',
          // Chips and footer are fixed rows and only the result list scrolls, so
          // neither can ever sit over the row the keyboard has highlighted.
          display: 'flex', flexDirection: 'column',
        }}>
          {loading && <div style={{ padding: '12px 14px', fontSize: 10.5, color: c.meta }}>Searching…</div>}

          {!loading && results && !hasAny && (
            <div style={{ padding: '14px', fontSize: 10.5, color: c.meta, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span>{includeClosed ? 'No results' : 'No live results'} for &ldquo;{query.trim()}&rdquo;</span>
              {!includeClosed && (
                <button
                  onMouseDown={e => { e.preventDefault(); setIncludeClosed(true); }}
                  style={chipStyle(false)}
                >🔎 Search completed &amp; closed too</button>
              )}
            </div>
          )}

          {!loading && hasAny && (
            <>
              {/* Category filter chips — keeps a busy result set easy to scan */}
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 5, padding: '9px 12px', flexShrink: 0,
                background: c.panel, borderBottom: `1px solid ${c.border}`,
              }}>
                <span onMouseDown={e => { e.preventDefault(); setCat('all'); }} style={chipStyle(cat === 'all')}>
                  All <span style={chipCount(cat === 'all')}>{totalCount}</span>
                </span>
                {categories.map(g => (
                  <span key={g.key} onMouseDown={e => { e.preventDefault(); setCat(g.key); }} style={chipStyle(cat === g.key)}>
                    {g.title} <span style={chipCount(cat === g.key)}>{g.total}</span>
                  </span>
                ))}
              </div>

              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
              {groups.map(g => (
                <div key={g.key}>
                  {/* Deliberately not sticky: the chip bar above it wraps to a
                      variable height, so there is no fixed offset a pinned header
                      could sit at without colliding with it. */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 7, padding: '7px 12px 5px',
                    background: c.groupBg, borderTop: `1px solid ${c.border}`, borderBottom: `1px solid ${c.hairline}`,
                  }}>
                    <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', color: c.meta }}>
                      {g.title}
                    </span>
                    <span style={{ fontSize: 8.5, fontWeight: 700, color: g.total > g.items.length ? c.pill.warn.fg : c.faint }}>
                      {g.total > g.items.length ? `showing ${g.items.length} of ${g.total}` : g.items.length}
                    </span>
                    {g.total > g.items.length && !expanded.has(g.key) && (
                      <button
                        onMouseDown={e => { e.preventDefault(); loadAll(g.key, includeClosed); }}
                        disabled={expanding === g.key}
                        style={{
                          marginLeft: 'auto', fontSize: 8.5, fontWeight: 700, cursor: 'pointer',
                          padding: '1px 7px', borderRadius: 999, border: `1px solid ${c.border}`,
                          background: c.panel, color: c.ref,
                        }}
                      >
                        {expanding === g.key ? 'Loading…' : `Show all ${g.total}`}
                      </button>
                    )}
                    {g.total > g.items.length && expanded.has(g.key) && (
                      <span style={{ marginLeft: 'auto', fontSize: 8.5, color: c.faint }}>
                        too many to list, narrow the search
                      </span>
                    )}
                  </div>
                  {g.items.map((item, i) => {
                    const idx = g.start + i;
                    const active = idx === activeIdx;
                    return (
                      <ResultRow
                        key={`${g.key}-${item.id ?? idx}`}
                        item={item}
                        c={c}
                        query={query.trim()}
                        active={active}
                        rowRef={active ? activeRowRef : undefined}
                        onHover={() => setActiveIdx(idx)}
                        onSelect={onSelect}
                      />
                    );
                  })}
                </div>
              ))}
              </div>
            </>
          )}

          {/* Scope footer — live-only by default; toggle to include completed/closed */}
          {!loading && results && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', flexShrink: 0,
              background: c.panel, borderTop: `1px solid ${c.border}`,
            }}>
              <label
                onMouseDown={e => e.preventDefault()}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 9.5, fontWeight: 600, color: c.meta, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={includeClosed}
                  onChange={e => setIncludeClosed(e.target.checked)}
                  style={{ width: 12, height: 12, cursor: 'pointer' }}
                />
                Include completed &amp; closed
              </label>
              {!includeClosed && (
                <span style={{ fontSize: 9, color: c.faint }}>live work only</span>
              )}
              {hasAny && (
                <span style={{ fontSize: 9, color: c.faint, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                  ↑↓ move · ↵ open · esc close
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
